import { BusinessObjectBase } from './base';
import { horseGroupSchema } from '@schemas/business-objects/horse-group';
import type { GroupMatch } from './group-match';
import type { HorseGroupMember } from './horse-group-member';
import type { GroupSubCategory, Id, Sex } from '../base-types';

export interface HorseGroupData {
  readonly id: Id;
  readonly name: string;
  readonly category: Sex;
  readonly subCategory?: GroupSubCategory;
  readonly legacyId?: number;
  readonly members?: HorseGroupMember[];
  readonly groupMatches?: GroupMatch[];
}

export interface HorseGroup extends Readonly<HorseGroupData> {}
type HorseGroupCreateWithChildrenData = Partial<
  Omit<HorseGroupData, 'members'>
> & { members?: Partial<HorseGroupMember>[] };

export class HorseGroup extends BusinessObjectBase {
  static readonly entityName = 'HorseGroup' as const;
  static readonly plural = 'horse-groups';
  static schema = horseGroupSchema;

  // --- Static CRUD methods ---

  static async find(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<HorseGroup[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params, undefined, options);
    return results.map((data) => new HorseGroup(data));
  }

  static async findOne(
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<HorseGroup | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return data ? new HorseGroup(data) : null;
  }

  static async findById(
    id: Id,
    filter?: any,
    options?: { quietErrors?: boolean },
  ): Promise<HorseGroup> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params, undefined, options);
    return new HorseGroup(data);
  }

  static async create(data: Omit<HorseGroupData, 'id'>): Promise<HorseGroup> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new HorseGroup(result);
  }

  static async createMany(
    data: Omit<HorseGroupData, 'id'>[],
  ): Promise<HorseGroup[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new HorseGroup(item));
  }

  static async createWithChildren(
    data: HorseGroupCreateWithChildrenData,
  ): Promise<HorseGroup> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new HorseGroup(result);
  }

  static async createManyWithChildren(
    data: HorseGroupCreateWithChildrenData[],
  ): Promise<HorseGroup[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new HorseGroup(item));
  }

  static async updateById(
    id: Id,
    updates: Partial<HorseGroupData>,
  ): Promise<HorseGroup> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new HorseGroup(result);
  }

  static async deleteById(id: Id): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
